import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import { Avatar, Box, Button, IconButton, Link, Stack, Tooltip, Typography, alpha } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { createBinaryFile, deleteBinaryFile, fetchBinaryFiles, patchBinaryFile } from '@sophocles/api/binaryFiles';
import UserAvatar from '@sophocles/components/UserAvatar';
import { EntityGrid } from '@sophocles/data-grids';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import { addToast } from '@sophocles/features/toast';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';

import BinaryFileDialog from '../dialogs/BinaryFileDialog';

// V2 list of files attached to an entity, scoped by (entityType,
// entityId, fileType).  Generic over those three — the same component
// backs customer attachments, customer logos, site photos, profile
// photos, etc.  The parent renders this only when the section is
// permitted (per `Boolean(settings.<section>)`); backend enforces
// per-(entity_type, file_type) write rules independently.
//
// Layout:
//   - EntityGrid over odl-app/binary-files/ (server-paginated; toolbar
//     + search + Upload create-action come from EntityGrid's chrome).
//   - Whole grid wrapper is a drop zone — drag-over surfaces a tinted
//     overlay with upload prompt.  Row actions (hover-revealed download /
//     edit / delete) live in an `extraColumns` actions column.
//
// Props:
//   entityType   : 'customer' | 'site' | 'sitegroup' | 'customuser'
//   entityId     : number
//   fileType     : 'attachment' | 'logo' | 'photo' | 'profile-photo'
//   uploadLabel? : button + dialog label (default 'Upload')
//   emptyText?   : empty-state copy
//   canWrite?    : if false, hides drop zone + edit + delete + upload
//                  (read-only embed; backend enforces independently)

// ---------- helpers ---------------------------------------------------------

const _EXTENSION_ICONS = {
  pdf: PictureAsPdfOutlinedIcon,
  png: ImageOutlinedIcon,
  jpg: ImageOutlinedIcon,
  jpeg: ImageOutlinedIcon,
  gif: ImageOutlinedIcon,
  webp: ImageOutlinedIcon,
  svg: ImageOutlinedIcon,
  bmp: ImageOutlinedIcon,
  heic: ImageOutlinedIcon,
  doc: DescriptionOutlinedIcon,
  docx: DescriptionOutlinedIcon,
  txt: DescriptionOutlinedIcon,
  rtf: DescriptionOutlinedIcon,
  md: DescriptionOutlinedIcon,
  xls: TableChartOutlinedIcon,
  xlsx: TableChartOutlinedIcon,
  csv: TableChartOutlinedIcon,
  ppt: SlideshowOutlinedIcon,
  pptx: SlideshowOutlinedIcon,
  zip: FolderZipOutlinedIcon,
  rar: FolderZipOutlinedIcon,
  '7z': FolderZipOutlinedIcon,
  tar: FolderZipOutlinedIcon,
  gz: FolderZipOutlinedIcon,
  mp4: VideoFileOutlinedIcon,
  mov: VideoFileOutlinedIcon,
  webm: VideoFileOutlinedIcon,
  avi: VideoFileOutlinedIcon,
};

// Fixed, non-brand file-type tints (Drive-style), independent of theme/tenant color.
const _EXTENSION_TINT = {
  pdf: '#d93025',
  png: '#1a73e8',
  jpg: '#1a73e8',
  jpeg: '#1a73e8',
  gif: '#1a73e8',
  webp: '#1a73e8',
  svg: '#1a73e8',
  bmp: '#1a73e8',
  heic: '#1a73e8',
  doc: '#1a73e8',
  docx: '#1a73e8',
  txt: '#5f6368',
  rtf: '#5f6368',
  md: '#5f6368',
  xls: '#188038',
  xlsx: '#188038',
  csv: '#188038',
  ppt: '#e8710a',
  pptx: '#e8710a',
  zip: '#5f6368',
  rar: '#5f6368',
  '7z': '#5f6368',
  tar: '#5f6368',
  gz: '#5f6368',
  mp4: '#9334e6',
  mov: '#9334e6',
  webm: '#9334e6',
  avi: '#9334e6',
};

function getExtension(filename) {
  if (!filename) return '';
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
}

function FileTypeIcon({ filename, size = 22 }) {
  const ext = getExtension(filename);
  const IconComponent = _EXTENSION_ICONS[ext] || InsertDriveFileOutlinedIcon;
  const tint = _EXTENSION_TINT[ext];
  return <IconComponent sx={{ fontSize: size, color: tint || 'text.secondary' }} />;
}

const _IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'heic', 'avif']);

function isImageFile(binaryFile) {
  const mime = binaryFile?.mime_type || '';
  if (mime.startsWith('image/')) return true;
  return _IMAGE_EXTENSIONS.has(getExtension(binaryFile?.original_filename || ''));
}

// Cell-sized thumbnail for image rows; falls back to the file-type icon.
// On hover, a Tooltip-style popover shows a larger preview so the user
// can scan the row without opening the file in a new tab.
function FileThumb({ binaryFile, size = 36 }) {
  const filename = binaryFile?.original_filename || '';
  if (isImageFile(binaryFile) && binaryFile.file_url) {
    return (
      <Tooltip
        placement="right"
        enterDelay={250}
        leaveDelay={50}
        slotProps={{
          tooltip: {
            sx: { p: 0.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider', maxWidth: 'none' },
          },
        }}
        title={
          <Box
            component="img"
            src={binaryFile.file_url}
            alt={filename}
            sx={{ display: 'block', maxWidth: 280, maxHeight: 280, borderRadius: 0.5 }}
          />
        }
      >
        <Avatar
          variant="rounded"
          src={binaryFile.file_url}
          alt={filename}
          sx={{
            width: size,
            height: size,
            bgcolor: theme => alpha(theme.palette.text.primary, 0.04),
            border: 1,
            borderColor: 'divider',
          }}
        />
      </Tooltip>
    );
  }
  return (
    <Avatar
      variant="rounded"
      sx={{
        width: size,
        height: size,
        bgcolor: theme => alpha(theme.palette.text.primary, 0.04),
        color: 'text.secondary',
      }}
    >
      <FileTypeIcon filename={filename} size={Math.round(size * 0.6)} />
    </Avatar>
  );
}

function userName(u) {
  if (!u || typeof u !== 'object') return null;
  const first = u.first_name ?? '';
  const last = u.last_name ?? '';
  const username = u.username;
  return [first, last].filter(Boolean).join(' ').trim() || username || null;
}

function UploaderChip({ user }) {
  const display = userName(user);
  if (!display) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <UserAvatar user={user} size={24} sx={{ fontSize: 12, bgcolor: 'primary.main' }} />
      <Typography variant="body2" noWrap>
        {display}
      </Typography>
    </Stack>
  );
}

// ---------- main component --------------------------------------------------

export default function BinaryFileGrid({ entityType, entityId, fileType, uploadLabel, emptyText, canWrite = true }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'edit', binaryFile?, prefillFile? }
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();
  // EntityGrid hands back gridState.refresh via onGridReady; captured here so
  // create/edit/delete can refetch page 0 after a mutation.
  const { onGridReady, refresh: refreshGrid } = useGridRefreshRef();
  // Controls which columns are visible.  Camera columns (camera_number,
  // camera_description) are always hidden here — this grid surfaces
  // generic attachments, not camera images; those fields are always null.
  // Persisted server-side per user, matching the sibling grids' idiom.
  const columnVisibility = useColumnVisibilityModel('binaryFilesGrid', {
    camera_number: false,
    camera_description: false,
  });

  // Entity scoping for the server-side list.  Serialized to filter[...]
  // by toJsonApiParams (via config.paramSerializer in EntityGrid).
  const extraParams = useMemo(
    () => ({
      ...(entityType !== undefined ? { entity_type: entityType } : {}),
      ...(entityId !== undefined ? { entity_id: entityId } : {}),
      ...(fileType !== undefined ? { file_type: fileType } : {}),
    }),
    [entityType, entityId, fileType],
  );

  // Wraps fetchBinaryFiles so rows carry a stable `id` field.
  // useServerDataGrid's getRowId reads row.id; the binary-files wire
  // response uses `guid` as its natural key.  Serialized extraParams
  // (filter[entity_type] etc.) arrive via `params` and pass through
  // fetchBinaryFiles's `...rest` spread to the API unchanged.
  const fetchFn = useCallback(async params => {
    const res = await fetchBinaryFiles(params);
    const rows = (res?.data ?? []).map(row => ({
      ...row,
      id: row.id ?? row.guid,
    }));
    return { data: rows, meta: { total: res?.meta?.total ?? rows.length } };
  }, []);

  const handleSaved = () => {
    setDialog(null);
    refreshGrid();
  };

  const handleDeleteClick = row => {
    confirmDelete(row, {
      entityLabel: t('binaryFiles.file', 'File'),
      onConfirm: async target => {
        try {
          await deleteBinaryFile(target.guid);
          refreshGrid();
        } catch (err) {
          dispatch(
            addToast({
              message: t('binaryFiles.deleteFailed', 'Failed to delete file. Please try again.'),
              level: 'error',
            }),
          );
        }
      },
    });
  };

  // ---- drag-drop --------------------------------------------------------

  const onDrop = useCallback(
    accepted => {
      if (!canWrite || !accepted?.length) return;
      // Open the dialog pre-populated with the dropped file.  Single-file
      // upload only — we ignore additional files in the drop.
      setDialog({ mode: 'create', prefillFile: accepted[0] });
    },
    [canWrite],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: !canWrite,
  });

  // ---- fieldOverrides ---------------------------------------------------

  const fieldOverrides = useMemo(
    () => ({
      // Render thumb + linked filename.  The descriptor emits file_url as
      // the file field; original_filename is a companion field on the row.
      file_url: {
        headerName: t('binaryFiles.col.file', 'File'),
        flex: 2,
        minWidth: 240,
        renderCell: params => {
          const filename = params.row.original_filename || params.value?.split('/').pop() || '—';
          const url = params.value;
          return (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, width: '100%' }}>
              <FileThumb binaryFile={params.row} />
              {url ? (
                <Link
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  variant="body2"
                  underline="hover"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                    fontWeight: 500,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  {filename}
                </Link>
              ) : (
                <Typography variant="body2" noWrap>
                  {filename}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      comment: {
        renderCell: params =>
          params.value ? (
            <Tooltip title={params.value} placement="top-start" enterDelay={400}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {params.value}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          ),
      },
      uploaded_by: {
        width: 200,
        renderCell: params => <UploaderChip user={params.value} />,
      },
      timestamp: {
        width: 150,
        renderCell: params => {
          if (!params.value) return null;
          const m = moment(params.value);
          return (
            <Tooltip title={m.format('L, LTS')}>
              <Typography variant="body2" color="text.secondary">
                {m.fromNow()}
              </Typography>
            </Tooltip>
          );
        },
      },
    }),
    [t],
  );

  // ---- extraColumns (actions) ------------------------------------------

  const extraColumns = useMemo(
    () => [
      {
        field: '__actions',
        headerName: '',
        width: canWrite ? 132 : 56,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => (
          <Stack
            className="binary-file-row-actions"
            direction="row"
            sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 120ms' }}
          >
            {params.row.file_url ? (
              <Tooltip title={t('actions.download', 'Download')}>
                <IconButton
                  size="small"
                  component="a"
                  href={params.row.file_url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                >
                  <DownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {canWrite ? (
              <>
                <Tooltip title={t('actions.edit', 'Edit')}>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setDialog({ mode: 'edit', binaryFile: params.row });
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('actions.delete', 'Delete')}>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteClick(params.row);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
          </Stack>
        ),
      },
    ],
    [t, canWrite],
  );

  // ---- mobile card (replaces listViewColumn) ---------------------------

  const renderMobileCard = useCallback(
    row => {
      const filename = row.original_filename || row.file_url?.split('/').pop() || '—';
      return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%', py: 1.5, px: 0.5 }}>
          <FileThumb binaryFile={row} size={40} />
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            {row.file_url ? (
              <Link
                href={row.file_url}
                target="_blank"
                rel="noreferrer"
                variant="body2"
                underline="hover"
                sx={{ fontWeight: 500, color: 'text.primary', wordBreak: 'break-all' }}
                onClick={e => e.stopPropagation()}
              >
                {filename}
              </Link>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                {filename}
              </Typography>
            )}
            {row.comment ? (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {row.comment}
              </Typography>
            ) : null}
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
              {row.uploaded_by ? <UploaderChip user={row.uploaded_by} /> : null}
              {row.timestamp ? (
                <Tooltip title={moment(row.timestamp).format('L, LTS')}>
                  <Typography variant="caption" color="text.secondary">
                    {moment(row.timestamp).fromNow()}
                  </Typography>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
          {canWrite ? (
            <Stack direction="row">
              <Tooltip title={t('actions.edit', 'Edit')}>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setDialog({ mode: 'edit', binaryFile: row });
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('actions.delete', 'Delete')}>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteClick(row);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null}
        </Stack>
      );
    },
    [t, canWrite],
  );

  // ---- empty state overlay ---------------------------------------------

  // DataGridPro slots.noRowsOverlay replaces the default "No rows" text
  // with the custom upload CTA from EmptyState.  Wrapped in useMemo so
  // the component reference is stable across renders (a new type on every
  // render would cause MUI to unmount/remount the overlay needlessly).
  const NoRowsOverlay = useMemo(
    () =>
      function BinaryFilesEmptyState() {
        return (
          <EmptyState canWrite={canWrite} emptyText={emptyText} onUploadClick={() => setDialog({ mode: 'create' })} />
        );
      },

    [canWrite, emptyText],
  );

  // ---- render -----------------------------------------------------------

  return (
    <Stack spacing={0}>
      <Box
        {...getRootProps({ onClick: e => e.stopPropagation() })}
        sx={{
          position: 'relative',
          '&:hover .MuiDataGrid-row .binary-file-row-actions': { opacity: { md: 1 } },
          '& .MuiDataGrid-row:hover .binary-file-row-actions': { opacity: 1 },
        }}
      >
        <input {...getInputProps()} />
        <EntityGrid
          basePath="binary-files"
          fetchFn={fetchFn}
          extraParams={extraParams}
          fieldOverrides={fieldOverrides}
          options={{
            searchPlaceholder: t('binaryFiles.searchPlaceholder', 'Search files…'),
            // Flat toolbar: this grid is always embedded in a section that
            // already supplies the surrounding card surface (SettingsSection),
            // so the toolbar shouldn't add its own competing bordered box —
            // matches the AlarmsSummaryCard/MaintenanceSummaryCard house chrome.
            toolbarVariant: 'flat',
            onGridReady,
            createAction: canWrite
              ? {
                  label: uploadLabel ?? t('binaryFiles.upload', 'Upload'),
                  icon: <UploadOutlinedIcon />,
                  onClick: () => setDialog({ mode: 'create' }),
                }
              : null,
            columnVisibility,
            extraColumns,
            mobile: { renderCard: renderMobileCard },
            gridProps: {
              autoHeight: true,
              disableColumnMenu: true,
              columnHeaderHeight: 44,
              slots: { noRowsOverlay: NoRowsOverlay },
            },
          }}
        />
        {isDragActive ? <DragOverlay /> : null}
      </Box>

      {dialog ? (
        <BinaryFileDialog
          open
          mode={dialog.mode}
          binaryFile={dialog.binaryFile}
          prefillFile={dialog.prefillFile}
          entityType={entityType}
          entityId={entityId}
          fileType={fileType}
          onClose={() => setDialog(null)}
          onCreate={createBinaryFile}
          onUpdate={patchBinaryFile}
          onSaved={handleSaved}
          titleCreate={uploadLabel}
        />
      ) : null}

      {deleteConfirmDialog}
    </Stack>
  );
}

// ---------- empty state -----------------------------------------------------

function EmptyState({ canWrite, emptyText, onUploadClick }) {
  const { t } = useTranslation();
  return (
    <Stack
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 6,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 30, color: 'primary.main' }} />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {emptyText ?? t('binaryFiles.empty', 'No files yet.')}
      </Typography>
      {canWrite ? (
        <Button startIcon={<UploadOutlinedIcon />} variant="outlined" size="small" onClick={onUploadClick}>
          {t('binaryFiles.upload', 'Upload')}
        </Button>
      ) : null}
    </Stack>
  );
}

// ---------- drag overlay ----------------------------------------------------

function DragOverlay() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        borderRadius: 1,
        backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
        border: 2,
        borderStyle: 'dashed',
        borderColor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      <Stack alignItems="center" spacing={1}>
        <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: 'primary.main' }} />
        <Typography variant="subtitle2" color="primary.main">
          {t('binaryFiles.dropToUpload', 'Drop file to upload')}
        </Typography>
      </Stack>
    </Box>
  );
}
